/**
 * 出图等待页的区块视图(2026-08-03)。
 *
 * ═══ 为什么等待页能画出「即将出的那张图」═════════════════════════════════════
 * 长图 RUNNING 期间后端没有任何逐块信号(ai 侧 gather 全并发渲完才一次性上传),
 * 但**规划本身**此刻就在本机:智能稿出图时 `markGenerated` 把逐块快照连着草稿
 * 落进 localStorage 镜像(2026-08-01 设计锚点⑤,为「单块重出」留的路)。
 * `generated.taskId` 对得上当前任务 → 草稿里的 blocks 就是这张图的真规划:
 * 块名、样式、谁过引擎、估高比例全有 —— 等待页照着它搭骨架墙,分毫不编。
 *
 * ★对不上(别台设备提交/手动稿已清/老草稿)→ 返 null,UI 落通用骨架。
 *   宁可不知道,不可编 —— 骨架墙上的每个块名都必须真来自这单的规划。
 */
import { blockCallsEngine } from "../../model/longImageEstimate";
import type { EngineContext } from "../../model/longImageEstimate";
import { STYLE_META } from "./blocks";
import { parseCanvasDraft } from "./canvasBridge";
import type { CanvasBlockForm, CanvasBuilderState } from "./canvasBridge";
import { estimateBlockHeight } from "./estimateHeight";

/** useCanvasBuilder 的镜像 key(那边是私有常量;此处只读,改名两处同步)。 */
const MIRROR_KEY = "gscq.poster.longimage.canvas.draft.v1";
/**
 * 手动/模板稿提交时按任务落的快照 key(2026-08-10)。
 * 智能稿有 canvas 镜像可反查,手动稿提交后 `clearDraft` 即清 —— 等待页对它一直只能落
 * 通用骨架,零 AI 块的任务也被说成「图块由 AI 出图 · 2~4 分钟」。提交那一刻把算好的
 * 视图连 taskId 存一份,两条路的等待页就都长在真规划上。
 */
const SNAPSHOT_KEY = "gscq.poster.longimage.generating.v1";

export interface GeneratingBlockView {
  id: string;
  /** 用户看的块名(空名回落样式展示名)。 */
  name: string;
  styleTag: string;
  /** STYLE_META 徽章文案(文案/图/参数…);未知样式回落 primitive。 */
  badge: string;
  /** 这一块会不会叫出图引擎(= 预估计费口径,blockCallsEngine 同一谓词)。 */
  engine: boolean;
  /** 750 设计空间估高(骨架墙按它等比,长得像即将出的那张)。 */
  height: number;
}

export interface GeneratingView {
  blocks: GeneratingBlockView[];
  engineCount: number;
}

/** 逐块映射的单一实现 —— 镜像路与快照路共用,报价谓词/估高不许各抄一份。 */
function viewOfBlocks(blocks: CanvasBlockForm[], ctx: EngineContext): GeneratingView | null {
  const list = (blocks ?? []).filter(Boolean);
  if (!list.length) return null;
  const views = list.map((b: CanvasBlockForm): GeneratingBlockView => {
    const meta = STYLE_META[b.styleTag];
    return {
      id: b.id,
      name: b.name?.trim() || meta?.label || b.styleTag,
      styleTag: b.styleTag,
      badge: meta?.badge ?? b.primitive,
      engine: blockCallsEngine(b, ctx),
      height: estimateBlockHeight(b),
    };
  });
  return { blocks: views, engineCount: views.filter((v) => v.engine).length };
}

/** 纯函数:草稿状态 + 任务 id → 等待页视图(快照对不上 → null)。 */
export function generatingViewOf(
  state: Pick<CanvasBuilderState, "blocks" | "generated" | "styleTokens"> | null | undefined,
  taskId: number
): GeneratingView | null {
  if (!state?.generated || state.generated.taskId !== taskId) return null;
  // ★与报价同环境:画布稿必带统一 DNA → 显式 B / 未指定块按「过引擎」标(2026-08-04 口径)
  return viewOfBlocks(state.blocks ?? [], { hasStyleTokens: !!state.styleTokens });
}

/**
 * 提交那一刻落快照(手动/模板稿路;智能稿有 canvas 镜像不必再写)。
 * ctx 必须与页内预估同一份 —— 快照上的引擎标就是报价口径,两处不同源就会
 * 出现「预估 0 图块、等待页却亮 AI 徽」的自相矛盾。落不了盘(隐私模式/超额)
 * 就静默放弃,等待页照旧走通用骨架 —— 宁可不知道,不可编。
 */
export function writeGeneratingSnapshot(
  taskId: number,
  blocks: CanvasBlockForm[],
  ctx: EngineContext
): void {
  try {
    const view = viewOfBlocks(blocks, ctx);
    if (!view) return;
    localStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ taskId, view }));
  } catch {
    /* 落不了盘 → 等待页落通用骨架 */
  }
}

function readSnapshot(taskId: number): GeneratingView | null {
  try {
    const stored = localStorage.getItem(SNAPSHOT_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { taskId?: unknown; view?: unknown };
    if (parsed.taskId !== taskId) return null;
    const v = parsed.view as GeneratingView | undefined;
    if (!v || !Array.isArray(v.blocks) || typeof v.engineCount !== "number") return null;
    return v;
  } catch {
    return null;
  }
}

/**
 * localStorage 壳:镜像是 `{draftId, raw}` 双层包裹(useCanvasBuilder.persistNow 同形),
 * raw 走 `parseCanvasDraft` 逐字段验型 —— 不自己再发明一套解析。
 * 任何一步失败(无镜像/坏 JSON/隐私模式/旧版草稿)一律 null,不抛。
 */
export function readGeneratingView(taskId: number): GeneratingView | null {
  // 提交时落的任务快照优先(手动/模板稿唯一的信息源;taskId 不对上就当没有)
  const snap = readSnapshot(taskId);
  if (snap) return snap;
  try {
    const stored = localStorage.getItem(MIRROR_KEY);
    if (!stored) return null;
    const mirror = JSON.parse(stored) as { raw?: unknown };
    if (typeof mirror.raw !== "string") return null;
    return generatingViewOf(parseCanvasDraft(mirror.raw), taskId);
  } catch {
    return null;
  }
}
